import { useState } from 'react';
import { Eye, MoreHorizontal, X, Shield, UserCog, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { StatusBadge } from './StatusBadge';
import { RoleBadge } from './RoleBadge';
import { User } from '../../types/user';
import { cn } from '../../lib/utils';
import { toast } from '../../components/ui/use-toast';
import { services } from '../../services';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUserApproval } from '../../hooks/useUserApproval';

interface UsersTableProps {
  users: User[];
  selectedUsers: string[];
  onSelectUser: (userId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onRefresh?: () => void;
}

export function UsersTable({
  users,
  selectedUsers,
  onSelectUser,
  onSelectAll,
  onRefresh,
}: UsersTableProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const { approveUser } = useUserApproval();

  const allSelected = users.length > 0 && selectedUsers.length === users.length;
  const someSelected = selectedUsers.length > 0 && selectedUsers.length < users.length;

  const setLoading = (userId: string, loading: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [userId]: loading }));
  };

  const handleApprove = async (user: User) => {
    setLoading(user.id, true);
    try {
      const result = await approveUser({
        id: user.id,
        walletAddress: user.walletAddress,
        country: 'US', // TODO: Get actual country from user data
        name: user.name || undefined,
      });

      if (result.success) {
        onRefresh?.();
        queryClient.invalidateQueries({ queryKey: ['users'] });
      }
      // Error handling is done in the useUserApproval hook
    } catch (error: unknown) {
      // Additional error handling if needed
    } finally {
      setLoading(user.id, false);
    }
  };

  // const handleReject = async (user: User) => {
  //   setLoading(user.id, true);
  //   try {
  //     const params = {
  //       userId: user.id,
  //       status: 'REJECTED',
  //     };
  //     const response = await services.post('/user/disableUser', params);
  //     if (response?.status === 200) {
  //       toast({
  //         description: `User ${user.name || user.walletAddress} rejected`,
  //         variant: 'default',
  //       });
  //       onRefresh?.();
  //       queryClient.invalidateQueries({ queryKey: ['users'] });
  //     } else {
  //       throw new Error('Failed to reject user');
  //     }
  //   } catch (error: unknown) {
  //     const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
  //     if (axiosError?.response?.status === 401 || axiosError?.response?.status === 403) {
  //       toast({ description: 'Unauthorized Request', variant: 'destructive' });
  //       localStorage.removeItem('token');
  //     } else {
  //       toast({
  //         description: axiosError?.response?.data?.message || 'Failed to reject user',
  //         variant: 'destructive',
  //       });
  //     }
  //   } finally {
  //     setLoading(user.id, false);
  //   }
  // };

  const handleSuspend = async (user: User) => {
    setLoading(user.id, true);
    try {
      const params = {
        userId: user.id,
        status: 'SUSPENDED',
      };

      const response = await services.post('/user/disableUser', params);

      if (response?.status === 200) {
        toast({
          description: `User ${user.name || user.walletAddress} suspended`,
          variant: 'default',
        });
        onRefresh?.();
        queryClient.invalidateQueries({ queryKey: ['users'] });
      } else {
        throw new Error('Failed to suspend user');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };

      if (axiosError?.response?.status === 401 || axiosError?.response?.status === 403) {
        toast({ description: 'Unauthorized Request', variant: 'destructive' });
        localStorage.removeItem('token');
      } else {
        toast({
          description: axiosError?.response?.data?.message || 'Failed to suspend user',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(user.id, false);
    }
  };

  const handleViewDetails = (user: User) => {
    navigate(`/users/${user.id}`);
  };

  const handleEditRole = (user: User) => {
    // Navigate to edit role page or open modal
    navigate(`/users/${user.id}/edit`);
  };

  const handleReviewKYC = (user: User) => {
    // Navigate to KYC review page
    navigate(`/users/${user.id}/kyc`);
  };

  const getInitials = (name: string | null) => {
    if (name && name !== '---') {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return '?';
  };

  return (
    <div className='glass-card rounded-xl overflow-hidden animate-slide-up'>
      <div className='overflow-x-auto -mx-1 sm:mx-0'>
        <table className='data-table w-full min-w-[800px]'>
          <thead>
            <tr className='bg-muted/30'>
              <th className='w-10 sm:w-12 px-2 sm:px-4 py-3'>
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(checked) => onSelectAll(!!checked)}
                  className={cn(someSelected && 'data-[state=checked]:bg-primary/50')}
                />
              </th>
              <th className='px-2 sm:px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                PROFILE
              </th>
              <th className='px-2 sm:px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground min-w-[120px]'>
                NAME
              </th>
              <th className='px-2 sm:px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell min-w-[180px]'>
                EMAIL
              </th>
              <th className='px-2 sm:px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                STATUS
              </th>
              <th className='px-2 sm:px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell'>
                ROLE
              </th>
              <th className='px-2 sm:px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden xl:table-cell min-w-[120px]'>
                WALLET
              </th>
              <th className='px-2 sm:px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground w-12'>
                VIEW
              </th>
              <th className='px-2 sm:px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground w-24 sm:w-32'>
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const isLoading = loadingStates[user.id];
              const isPending = user.status === 'PENDING' || user.status === 'pending';

              return (
                <tr key={user.id} className='group'>
                  <td className='px-2 sm:px-4'>
                    <Checkbox
                      checked={selectedUsers.includes(user.id)}
                      onCheckedChange={(checked) => onSelectUser(user.id, !!checked)}
                    />
                  </td>
                  <td className='px-2 sm:px-4'>
                    <Avatar className='w-8 h-8 sm:w-10 sm:h-10 bg-primary/20 border-2 border-primary/30'>
                      {user.profile && <AvatarImage src={user.profile} alt={user.name || ''} />}
                      <AvatarFallback className='bg-primary/20 text-primary text-xs sm:text-sm font-semibold'>
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                  </td>
                  <td className='px-2 sm:px-4'>
                    <span className='font-medium text-foreground text-sm sm:text-base truncate block max-w-[120px] sm:max-w-none'>
                      {user.name || '---'}
                    </span>
                  </td>
                  <td className='px-2 sm:px-4 hidden md:table-cell'>
                    <span className='text-muted-foreground text-sm truncate block max-w-[180px]'>
                      {user.email || '---'}
                    </span>
                  </td>
                  <td className='px-2 sm:px-4'>
                    <StatusBadge status={user.status} />
                  </td>
                  <td className='px-2 sm:px-4 hidden lg:table-cell'>
                    <RoleBadge role={user.role.roleName} />
                  </td>
                  <td className='px-2 sm:px-4 hidden xl:table-cell'>
                    <code className='font-mono text-xs sm:text-sm text-muted-foreground bg-muted/50 px-2 py-1 rounded'>
                      {user.walletAddress
                        ? `${user.walletAddress.slice(0, 6)}...${user.walletAddress.slice(-4)}`
                        : '---'}
                    </code>
                  </td>
                  <td className='px-2 sm:px-4'>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={() => handleViewDetails(user)}
                      className='h-7 w-7 sm:h-8 sm:w-8'
                    >
                      <Eye className='w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground' />
                    </Button>
                  </td>
                  <td className='px-2 sm:px-4'>
                    {isLoading ? (
                      <Loader2 className='w-4 h-4 animate-spin' />
                    ) : isPending ? (
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => handleApprove(user)}
                        className='gap-1 text-xs sm:text-sm px-3 sm:px-4 border-primary text-primary hover:bg-primary/10 hover:text-primary font-medium'
                      >
                        Approve
                      </Button>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant='ghost' size='icon' className='h-7 w-7 sm:h-8 sm:w-8'>
                            <MoreHorizontal className='w-3 h-3 sm:w-4 sm:h-4' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleViewDetails(user)}>
                            <Eye className='w-4 h-4 mr-2' />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditRole(user)}>
                            <UserCog className='w-4 h-4 mr-2' />
                            Edit Role
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleReviewKYC(user)}>
                            <Shield className='w-4 h-4 mr-2' />
                            Review KYC
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className='text-destructive'
                            onClick={() => handleSuspend(user)}
                          >
                            <X className='w-4 h-4 mr-2' />
                            Suspend User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
